import { Carte, EnteteEcran } from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { PanneauBpf } from '../components/documents/PanneauBpf';
import { usePerimetre } from '../site/usePerimetre';
import { useReferentiels, type RubriqueBpf } from '../lib/useDocuments';

const SOURCES_BPF: Record<RubriqueBpf['origine'], string> = {
  plateforme: 'calculé par la plateforme',
  comptabilite: 'à reprendre de la comptabilité',
  declaratif: 'à déclarer',
};

/** Ce que la ligne attend, quand le libellé ne le dit pas : seuls les effectifs en ont besoin. */
const NATURES_BPF: Record<RubriqueBpf['nature'], string> = {
  texte: '',
  euros: '',
  pourcentage: '',
  stagiaires: 'nombre',
  personnes: 'nombre',
  heures: 'heures',
};

/**
 * Une ligne du formulaire. Le référentiel sépare l'effectif et les heures en
 * deux rubriques ; le formulaire, lui, les demande sur la même ligne.
 */
interface LigneFormulaire {
  rubrique: RubriqueBpf;
  natures: string[];
}

function lignesParCadre(rubriques: RubriqueBpf[]): [string, LigneFormulaire[]][] {
  const groupes = new Map<string, LigneFormulaire[]>();
  for (const rubrique of rubriques) {
    const lignes = groupes.get(rubrique.cadre) ?? [];
    const precedente = lignes[lignes.length - 1];
    const nature = NATURES_BPF[rubrique.nature];
    if (
      precedente &&
      precedente.rubrique.libelle === rubrique.libelle &&
      precedente.rubrique.repere === rubrique.repere
    ) {
      if (nature) precedente.natures.push(nature);
    } else {
      lignes.push({ rubrique, natures: nature ? [nature] : [] });
    }
    groupes.set(rubrique.cadre, lignes);
  }
  return [...groupes];
}

export function BpfPage(): JSX.Element {
  const { site } = usePerimetre();

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Bilan pédagogique et financier"
        sousTitre="Pôles pédagogique et facturation · récapitulatif annuel de l’organisme, à saisir au téléservice de la DGEFP"
      />

      <BarreSites />

      <PanneauBpf site={site} />

      <RubriquesBpf />
    </div>
  );
}

function RubriquesBpf(): JSX.Element {
  const referentiels = useReferentiels();
  const bpf = referentiels.data?.bpf;
  const cadres = lignesParCadre(bpf?.rubriques ?? []);

  return (
    <Carte titre={bpf ? `Rubriques du formulaire · ${bpf.version}` : 'Rubriques du formulaire'} sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {cadres.map(([cadre, lignes]) => (
            <section key={cadre} aria-labelledby={`cadre-bpf-${cadre}`} className="min-w-0">
              <h3 id={`cadre-bpf-${cadre}`} className="mb-1.5 text-[11px] font-semibold text-codialis-navy">
                {bpf?.cadres[cadre] ?? cadre}
              </h3>
              <ul className="space-y-1">
                {lignes.map(({ rubrique, natures }, rang) => (
                  <li key={rubrique.cle}>
                    {rubrique.groupe && rubrique.groupe !== lignes[rang - 1]?.rubrique.groupe && (
                      <div className="mb-1 mt-1.5 text-[10px] italic text-codialis-gray">{rubrique.groupe}</div>
                    )}
                    <div
                      className={`flex flex-wrap gap-x-2 rounded border px-2 py-1 text-[11px] ${
                        rubrique.origine === 'plateforme'
                          ? 'border-codialis-mint/40 bg-codialis-mint/15 text-codialis-navy'
                          : 'border-gray-200 bg-gray-50 text-codialis-gray'
                      }`}
                    >
                      {rubrique.repere && <span className="shrink-0 font-semibold">{rubrique.repere}</span>}
                      <span className="min-w-0 flex-[1_1_12rem]">
                        {rubrique.libelle}
                        {natures.length > 0 && <span className="text-codialis-gray"> ({natures.join(', ')})</span>}
                      </span>
                      <span className="ml-auto shrink-0 text-[10px] text-codialis-gray">
                        {rubrique.horsFormulaire ? 'hors formulaire' : SOURCES_BPF[rubrique.origine]}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <p className="text-[11px] text-codialis-gray">
          En vert, les rubriques que la plateforme calcule depuis les dossiers et les
          émargements. Les autres viennent de la comptabilité ou d’une déclaration :
          elles sortent vides, jamais à zéro. Les lignes « hors formulaire » aident au
          rapprochement et ne se recopient pas.
        </p>
      </div>
    </Carte>
  );
}
